import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import { Box, Button } from '@mui/material';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MailIcon from '@mui/icons-material/Mail';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MoreIcon from '@mui/icons-material/MoreVert';
import Avatar from '@mui/material/Avatar';

// Custom Hooks & Constants
import useAuth from '../../hooks/useAuth';
import { SURVEY_NAV_ROLES } from '../../constants/roles';

export default function PrimarySearchAppBar(props) {
  // 🔑 ปรับมาใช้ window.location.href สำหรับการนำทางแบบ Full Reload
  const navigateTo = (path, newTab = false) => {
    if (newTab) {
      window.open(path, '_blank');
    } else {
      window.location.href = path;
    }
  };

  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
  const [navLinks, setNavLinks] = useState([]);
  const [navHeader, setNavHeader] = useState('');

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  // Custom Hooks
  const { user, setUser } = useAuth();

  // 🛡️ เช็คสิทธิ์กลุ่มงานสำรวจ/แผนที่ภาษี
  const userRoles = Array.isArray(user?.roles) 
    ? user.roles.map(String) 
    : [String(user?.roles || '')];
  const hasSurveyAccess = userRoles.some(role => SURVEY_NAV_ROLES.includes(role));

  useEffect(() => {
    const navObj = props.navList?.find(({ route }) => {
      let regexPattern = new RegExp("^" + route + ".*");
      return location.pathname.match(regexPattern);
    });
    if (navObj !== undefined) {
      setNavLinks(navObj.navLinks);
      setNavHeader(navObj.header);
    } else {
      setNavLinks([]);
    }
  }, [location.pathname, props.navList]);

  const handleLogout = (event) => {
    event.preventDefault();
    localStorage.removeItem('token');
    setUser(null);
    window.location.href = '/';
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  // Menu หลักฝั่งขวา
  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={() => { handleMenuClose(); navigateTo('/profile'); }}>โปรไฟล์</MenuItem>

      {/* 🟢 เมนูส่วนการสำรวจ แสดงเฉพาะกลุ่มที่มีสิทธิ์ */}
      {hasSurveyAccess && (
        <MenuItem onClick={() => { handleMenuClose(); navigateTo('/survey/surveylist'); }}>
          การสำรวจที่ดินและสิ่งปลูกสร้าง
        </MenuItem>
      )}
      {hasSurveyAccess && (
        <MenuItem onClick={() => { handleMenuClose(); navigateTo('/survey/billboardsurveylist'); }}>
          การสำรวจป้าย
        </MenuItem>
      )}

      <MenuItem onClick={handleLogout}>ออกจากระบบ</MenuItem>
    </Menu>
  );

  const mobileMenuId = 'primary-search-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem>
        <IconButton size="large" aria-label="show 4 new mails" color="inherit">
          <Badge badgeContent={0} color="error">
            <MailIcon />
          </Badge>
        </IconButton>
        <p>ข้อความ</p>
      </MenuItem>
      <MenuItem>
        <IconButton size="large" aria-label="show 17 new notifications" color="inherit">
          <Badge badgeContent={0} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <p>การแจ้งเตือน</p>
      </MenuItem>
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls={menuId}
          aria-haspopup="true"
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <p>โปรไฟล์</p>
      </MenuItem>
    </Menu>
  );

  return (
    <div>
      <div id="topNav">
        <Box sx={{ flexGrow: 1 }}>
          <AppBar style={{ backgroundColor: "#025464" }}>
            <Toolbar>
              <Button
                onClick={() => { navigateTo('/'); }}
                style={{ color: 'white' }}
              >
                <Avatar
                  sx={{ margin: '10px' }}
                  src="/suratcity-logo.png"
                />
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#fff' }}>
                  {navHeader}
                </h3>
              </Button>

              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                {navLinks?.length > 0 &&
                  navLinks.map((link, index) => (
                    <Button
                      key={index}
                      onClick={() => { navigateTo(link.path); }}
                      sx={{ my: 2, color: 'white', display: 'block' }}
                    >
                      {link.text}
                    </Button>
                  ))}
              </Box>

              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <IconButton size="large" aria-label="show 4 new mails" color="inherit">
                  <Badge badgeContent={0} color="error">
                    <MailIcon />
                  </Badge>
                </IconButton>
                <IconButton size="large" aria-label="show 17 new notifications" color="inherit">
                  <Badge badgeContent={0} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
                <IconButton
                  size="large"
                  edge="end"
                  aria-label="account of current user"
                  aria-controls={menuId}
                  aria-haspopup="true"
                  onClick={handleProfileMenuOpen}
                  color="inherit"
                >
                  <AccountCircle />
                </IconButton>
              </Box>
              <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  size="large"
                  aria-label="show more"
                  aria-controls={mobileMenuId}
                  aria-haspopup="true"
                  onClick={handleMobileMenuOpen}
                  color="inherit"
                >
                  <MoreIcon />
                </IconButton>
              </Box>
            </Toolbar>
          </AppBar>
          {renderMobileMenu}
          {renderMenu}
        </Box>
      </div>
    </div>
  );
}